<template>
  <div>
    <div class="d-flex justify-space-between align-center mb-4">
      <!-- View toggle -->
      <div class="d-flex align-center">
        <v-chip
          v-if="$store.state.statusFilter"
          class="mr-4"
          close
          @click="$store.dispatch('clearStatusFilter')"
        >
          {{ $store.state.statusFilter }}
          <template v-slot:prepend>
            <v-icon small :color="getStatusColor($store.state.statusFilter)">
              mdi-filter
            </v-icon>
          </template>
        </v-chip>
      </div>

      <v-btn-toggle v-model="viewMode" mandatory>
        <v-btn value="list">
          <v-icon>mdi-view-list</v-icon>
        </v-btn>
        <v-btn value="grid">
          <v-icon>mdi-view-grid</v-icon>
        </v-btn>
      </v-btn-toggle>
    </div>

    <!-- List View -->
    <v-data-table
      v-if="viewMode === 'list'"
      :headers="headers"
      :items="filteredSensors"
      :items-per-page="10"
      class="elevation-0"
      @click:row="$emit('click:row', $event)"
    >
      <template v-slot:[`item.lastSeen`]="{ item }">
        {{ formatLastSeen(item.lastSeen) }}
      </template>

      <template v-slot:[`item.deviceStatus`]="{ item }">
        <v-chip
          small
          :color="getStatusColor(deviceStatus(item))"
          text-color="white"
        >
          {{ deviceStatus(item) }}
        </v-chip>
      </template>

      <template v-slot:[`item.rotationAngle`]="{ item }">
        <span>{{ item.rotationAngle }}°</span>
      </template>

      <template v-slot:[`item.alerts`]="{ item }">
        <div class="d-flex align-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                large
                :color="item.alerts > 0 ? 'red' : 'green'"
                v-bind="attrs"
                v-on="on"
              >
                {{ item.alerts > 0 ? "mdi-alert-circle" : "mdi-check-circle" }}
              </v-icon>
            </template>
            <span>{{
              item.alerts > 0 ? `${item.alerts} alerts` : "No alerts"
            }}</span>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:[`item.batteryLevel`]="{ item }">
        <div class="d-flex align-center">
          <BatteryIndicator
            :percentage="item.batteryLevel"
            :scale="0.8"
            class="mr-2"
          />
          <span
            :class="{
              'success--text': item.batteryLevel > 60,
              'warning--text':
                item.batteryLevel <= 60 && item.batteryLevel > 20,
              'error--text': item.batteryLevel <= 20,
            }"
          >
          </span>
        </div>
      </template>

      <template v-slot:[`item.temperature`]="{ item }">
        <div class="d-flex align-center">
          <TemperatureIndicator
            :temperature="Math.round(item.temperature)"
            :scale="0.8"
            :animate="false"
            class="mr-2"
          />
          <span
            :class="{
              'success--text': item.temperature < 30,
              'warning--text': item.temperature >= 30 && item.temperature < 35,
              'error--text': item.temperature >= 35,
            }"
          >
          </span>
        </div>
      </template>

      <template v-slot:[`item.connectivity`]="{ item }">
        <div class="d-flex align-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                large
                v-bind="attrs"
                v-on="on"
                :color="item.status === 'Active' ? 'green' : 'grey'"
              >
                {{ item.status === "Active" ? "mdi-wifi" : "mdi-wifi-off" }}
              </v-icon>
            </template>
            <span>{{ item.status === "Active" ? "Online" : "Offline" }}</span>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-btn text small color="primary" @click="$emit('view-details', item)">
          View Details
        </v-btn>
      </template>
    </v-data-table>

    <!-- Grid View -->
    <v-row v-else>
      <v-col
        v-for="sensor in filteredSensors"
        :key="sensor.id"
        cols="12"
        sm="6"
        md="4"
        lg="4"
      >
        <v-card
          class="sensor-card"
          outlined
          elevation="0"
          @click="$emit('view-details', sensor)"
        >
          <v-card-title class="d-flex justify-space-between">
            <div class="d-flex align-center">
              <span class="mr-2">Device {{ sensor.id }}</span>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    :color="sensor.status === 'Active' ? 'green' : 'grey'"
                  >
                    {{
                      sensor.status === "Active" ? "mdi-wifi" : "mdi-wifi-off"
                    }}
                  </v-icon>
                </template>
                <span>{{
                  sensor.status === "Active" ? "Online" : "Offline"
                }}</span>
              </v-tooltip>
            </div>
            <v-chip
              small
              :color="getStatusColor(deviceStatus(sensor))"
              text-color="white"
            >
              {{ deviceStatus(sensor) }}
            </v-chip>
          </v-card-title>

          <v-card-text>
            <div class="sensor-grid-details">
              <div class="detail-row">
                <span class="detail-label">Last Seen:</span>
                <span>{{ formatLastSeen(sensor.lastSeen) }}</span>
              </div>

              <div class="detail-row">
                <span class="detail-label">Alerts:</span>
                <div class="d-flex align-center">
                  <v-icon :color="sensor.alerts > 0 ? 'red' : 'green'" small>
                    {{
                      sensor.alerts > 0
                        ? "mdi-alert-circle"
                        : "mdi-check-circle"
                    }}
                  </v-icon>
                  <span class="ml-2">{{
                    sensor.alerts > 0 ? `${sensor.alerts} alerts` : "No alerts"
                  }}</span>
                </div>
              </div>

              <div class="detail-row">
                <span class="detail-label">Rotation:</span>
                <span>{{ sensor.rotationAngle }}°</span>
              </div>

              <div class="detail-row">
                <span class="detail-label">Battery:</span>
                <div class="d-flex align-center">
                  <BatteryIndicator
                    :percentage="sensor.batteryLevel"
                    :scale="0.8"
                  />
                  <span
                    class="ml-2"
                    :class="{
                      'success--text': sensor.batteryLevel > 60,
                      'warning--text':
                        sensor.batteryLevel <= 60 && sensor.batteryLevel > 20,
                      'error--text': sensor.batteryLevel <= 20,
                    }"
                  >
                    {{ sensor.batteryLevel }}%
                  </span>
                </div>
              </div>

              <div class="detail-row">
                <span class="detail-label">Temperature:</span>
                <div class="d-flex align-center">
                  <TemperatureIndicator
                    :temperature="Math.round(sensor.temperature)"
                    :scale="0.8"
                    :animate="false"
                  />
                  <span
                    class="ml-2"
                    :class="{
                      'success--text': sensor.temperature < 30,
                      'warning--text':
                        sensor.temperature >= 30 && sensor.temperature < 35,
                      'error--text': sensor.temperature >= 35,
                    }"
                  >
                    {{ Math.round(sensor.temperature) }}°C
                  </span>
                </div>
              </div>

              <div class="detail-row">
                <span class="detail-label">Connectivity:</span>
                <div class="d-flex align-center">
                  <v-icon
                    small
                    :color="sensor.status === 'Active' ? 'green' : 'grey'"
                    class="mr-1"
                  >
                    {{
                      sensor.status === "Active" ? "mdi-wifi" : "mdi-wifi-off"
                    }}
                  </v-icon>
                  <span>{{
                    sensor.status === "Active" ? "Online" : "Offline"
                  }}</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import BatteryIndicator from "./BatteryIndicator.vue";
import TemperatureIndicator from "./TemperatureIndicator.vue";

export default {
  name: "SensorTable",

  components: {
    BatteryIndicator,
    TemperatureIndicator,
  },

  props: {
    sensors: {
      type: Array,
      required: true,
    },
    headers: {
      type: Array,
      required: true,
    },
    statusFilter: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      viewMode: "list",
    };
  },

  computed: {
    filteredSensors() {
      if (!this.statusFilter) return this.sensors;

      // Filter sensors based on status
      let filtered = this.sensors.filter((sensor) => {
        const status = this.deviceStatus(sensor);
        return status === this.statusFilter;
      });
      
      // For Critical status, sort to ensure offline devices appear last
      if (this.statusFilter === 'Critical') {
        filtered.sort((a, b) => {
          // Active devices come first, Inactive (offline) devices last
          if (a.status === 'Active' && b.status === 'Inactive') return -1;
          if (a.status === 'Inactive' && b.status === 'Active') return 1;
          return 0;
        });
      }
      
      return filtered;
    },

    deviceStatus() {
      return (sensor) => {
        // Define what makes a sensor Critical, Warning, or Secure
        if (sensor.rotationAngle >= 45) return "Critical"; // High angle = Critical regardless of connectivity
        if (sensor.alerts > 0) return "Warning";
        return "Secure";
      };
    },
  },

  methods: {
    formatLastSeen(timestamp) {
      if (!timestamp) return "Never";
      const date = new Date(timestamp);
      const now = new Date();
      const diffMinutes = Math.floor((now - date) / (1000 * 60));

      if (diffMinutes < 1) return "Just now";
      if (diffMinutes < 60) return `${diffMinutes}m ago`;
      if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
      return date.toLocaleDateString();
    },
    clearFilter() {
      console.log("Clearing filter");
      this.$store.dispatch("updateStatusFilter", null);
    },
    filterByStatus(status) {
      console.log("Filtering by status:", status);
      this.$store.dispatch("updateStatusFilter", status);
    },
    getStatusColor(status) {
      switch (status) {
        case "Secure":
          return "#4CAF50"; // Green
        case "Warning":
          return "#FFC107"; // Yellow
        case "Critical":
          return "#F44336"; // Red
        default:
          return "grey";
      }
    },
  },
};
</script>

<style scoped>
.sensor-card {
  height: 100%;
  border: 1px solid rgba(0, 0, 0, 0.12);
  transition: all 0.2s ease;
  cursor: pointer;
}

.sensor-card:hover {
  border-color: rgba(0, 0, 0, 0.24);
  background-color: rgba(0, 0, 0, 0.02);
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.sensor-grid-details {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-label {
  color: #666;
  font-size: 0.9rem;
}

.green-chip {
  background-color: #4caf50 !important;
}

.v-data-table {
  background: transparent !important;
}

.v-data-table >>> .v-data-table__wrapper {
  overflow-x: auto;
}

.v-data-table >>> tbody tr {
  cursor: pointer;
}

.v-data-table >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.03);
}
</style>
